import {
	AGENT_EDIT_CHROME_DATA,
	getAgentEditChromeDynamicConfig,
} from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { agentEditChromePluginKey } from '../../agent-edit-chrome/plugin-key';
import { getAgentEditChromePresentation } from '../../agent-edit-chrome/presentation';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';

/**
 * Stamps the shared post-apply chrome with the union of already-highlighted
 * ranges, remapped session positions, and the current write. SHOW replaces
 * the treatment, so every producer must send the full set.
 */
export function stampUnifiedAgentEditChrome(
	tr: Transaction,
	state: EditorState,
	currentRange?: { from: number; to: number },
): Transaction {
	if (!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1')) {
		return tr;
	}

	// Do not remap existing highlight ranges through this document write.
	// The replace usually overwrites those positions; mapping would shrink
	// the union down to the node just written. The chrome plugin skips
	// mapping for AI writes and unions this SHOW with the ranges it already has.
	const existing = (agentEditChromePluginKey.getState(state)?.ranges ?? []).filter(
		({ from, to }) => to > from,
	);
	const fromSession = (aiSessionContextPluginKey.getState(state)?.aiContentPositions ?? [])
		.map(({ startPos: from, endPos: to }) => ({ from, to }))
		.filter(({ from, to }) => to > from);
	const current = currentRange && currentRange.to > currentRange.from ? [currentRange] : [];
	const ranges = [...existing, ...fromSession, ...current];
	if (!ranges.length) {
		return tr;
	}

	return tr.setMeta(AGENT_EDIT_CHROME_DATA, {
		...getAgentEditChromeDynamicConfig(),
		...getAgentEditChromePresentation(aiExperienceDecorationPluginKey.getState(state)?.agentInfo),
		ranges,
	});
}
